import { getSafeSupabaseClient, getCurrentWorkspaceId } from './supabaseSync';
import { getRootQueueIdForWorkspace } from './queueRouting';
import { dispatchPrioritizationDashboardRefresh } from './prioritizationDashboardRefreshEvents';
import { requireAuthUser } from './requireAuthUser';

/** `source` is stored on `items.source` for traceability */
export type PlanConditionItemSource = 'backplan_node' | 'roadmap_node';

/**
 * Inserts an `items` row for a plan-condition node title, then callers typically
 * set `backplan_nodes.item_id` / `roadmap_nodes.item_id` to the returned id.
 */
export async function createWorkspaceItemFromPlanCondition(opts: {
  title: string;
  source: PlanConditionItemSource;
}): Promise<{ itemId: string | null; error: string | null }> {
  const supabase = getSafeSupabaseClient();
  const workspaceId = getCurrentWorkspaceId();
  if (!supabase || !workspaceId) {
    return { itemId: null, error: 'Workspace not available.' };
  }

  const title = opts.title.trim().slice(0, 500) || 'Untitled condition';
  const id = crypto.randomUUID();
  // Migration 751: the items insert policy requires created_by = auth.uid()
  // (and created_by_user_id, when set, to match) — attribution is the
  // signed-in person, never a display label.
  const { user: authUser } = await requireAuthUser(supabase);
  if (!authUser) {
    return { itemId: null, error: 'You are signed out. Sign in and try again.' };
  }
  const userId = authUser.id;
  const queueId = getRootQueueIdForWorkspace(workspaceId);

  const now = new Date().toISOString();

  const row: Record<string, unknown> = {
    id,
    workspace_id: workspaceId,
    title,
    description: '',
    lifecycle_state: 'item',
    lifecycle_stage: 'shaping',
    exit_status: 'active',
    source: opts.source,
    tags: [],
    created_by: userId,
    created_by_user_id: userId,
    created_at: now,
    updated_at: now,
  };

  if (queueId) {
    row.queue_id = queueId;
  }

  const { error } = await supabase.from('items').insert(row);

  if (error) {
    console.error('[createWorkspaceItemFromPlanCondition]', error);
    return { itemId: null, error: error.message ?? 'Could not create item.' };
  }

  dispatchPrioritizationDashboardRefresh({ source: `plan-condition-item:${opts.source}` });

  return { itemId: id, error: null };
}
